(() => {
  const ready = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  const galleryControllers = new WeakMap();

  const getGalleryController = (root) => {
    if (galleryControllers.has(root)) return galleryControllers.get(root);

    const gallery = root.querySelector('[data-product-gallery]');
    const main = gallery?.querySelector('.product-gallery-main');
    if (!main) return null;

    const mediaItems = Array.from(main.querySelectorAll('[data-media-id]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const status = main.querySelector('[data-gallery-status]');
    const statusText = status?.querySelector('[data-gallery-status-text]');
    const retryButton = status?.querySelector('[data-gallery-retry]');
    const imageRequests = new WeakMap();

    let selectionVersion = 0;
    let requestedId = null;
    let displayed = mediaItems.find((media) => !media.hidden);

    const setStatus = (message = '', retry = false) => {
      main.setAttribute('aria-busy', String(Boolean(message) && !retry));

      if (status) status.hidden = !message;
      if (statusText) statusText.textContent = message;
      if (retryButton) retryButton.hidden = !retry;
    };

    const prepareImage = (media, urgent = false, retry = false) => {
      const img = media?.querySelector('img');
      if (!img) return Promise.resolve();

      const slotWidth = main.getBoundingClientRect().width;
      if (slotWidth > 0) img.sizes = `${Math.ceil(slotWidth)}px`;

      img.setAttribute('fetchpriority', urgent ? 'high' : 'low');

      const existing = imageRequests.get(img);
      if (existing) return existing;

      const request = new Promise((resolve, reject) => {
        let settled = false;
        let decoding = false;
        let timeout;

        const clean = () => {
          clearTimeout(timeout);
          img.removeEventListener('load', loaded);
          img.removeEventListener('error', failed);
        };

        const failed = () => {
          if (settled) return;

          settled = true;
          clean();

          reject(new Error('Product image could not be loaded'));
        };

        const loaded = async () => {
          if (settled || decoding) return;

          if (!img.naturalWidth) {
            failed();
            return;
          }

          decoding = true;

          if (typeof img.decode === 'function') {
            try {
              await img.decode();
            } catch {
              if (!img.complete || !img.naturalWidth) {
                failed();
                return;
              }
            }
          }

          if (settled) return;

          settled = true;
          clean();
          resolve();
        };

        img.addEventListener('load', loaded);
        img.addEventListener('error', failed);

        timeout = setTimeout(failed, 20000);
        img.loading = 'eager';

        if (retry && img.complete && !img.naturalWidth) {
          const src = img.getAttribute('src');
          if (src) img.setAttribute('src', src);
        }

        if (img.complete && img.naturalWidth) loaded();
      });

      imageRequests.set(img, request);

      request.then(
        () => imageRequests.delete(img),
        () => imageRequests.delete(img)
      );

      return request;
    };

    const commit = (target) => {
      mediaItems.forEach((media) => {
        media.hidden = media !== target;

        if (media !== target) {
          media.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      displayed = target;

      thumbs.forEach((thumb) => {
        const active = thumb.dataset.galleryThumb === target.dataset.mediaId;

        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };

    const show = async (mediaId, retry = false) => {
      const target = mediaItems.find(
        (media) => media.dataset.mediaId === String(mediaId)
      );

      if (!target) return;

      const version = ++selectionVersion;
      requestedId = target.dataset.mediaId;

      if (target === displayed) {
        setStatus();
        return;
      }

      if (!target.querySelector('img')) {
        commit(target);
        setStatus();
        return;
      }

      setStatus('Loading photo…');

      try {
        await prepareImage(target, true, retry);

        if (version !== selectionVersion || !root.isConnected) return;

        commit(target);
        setStatus();
      } catch {
        if (version !== selectionVersion || !root.isConnected) return;

        setStatus('Photo could not load.', true);
      }
    };

    retryButton?.addEventListener('click', () => {
      if (requestedId) show(requestedId, true);
    });

    thumbs.forEach((thumb) => {
      const warm = () => {
        const target = mediaItems.find(
          (media) => media.dataset.mediaId === thumb.dataset.galleryThumb
        );

        if (target && target !== displayed) {
          prepareImage(target).catch(() => {});
        }
      };

      thumb.addEventListener('pointerenter', warm);
      thumb.addEventListener('focus', warm);
      thumb.addEventListener('pointerdown', warm, { passive: true });
    });

    const warmRemaining = async () => {
      const connection = navigator.connection;

      if (
        connection?.saveData ||
        /(^|-)2g$/.test(connection?.effectiveType || '')
      ) {
        return;
      }

      const activeIndex = mediaItems.indexOf(displayed);
      const ordered = mediaItems
        .slice(activeIndex + 1)
        .concat(mediaItems.slice(0, activeIndex));

      for (const media of ordered) {
        if (!root.isConnected) return;
        if (media === displayed || !media.querySelector('img')) continue;
        if (main.getAttribute('aria-busy') === 'true') return;

        try {
          await prepareImage(media);
        } catch {}
      }
    };

    const scheduleWarmup = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmRemaining, { timeout: 1500 });
      } else {
        setTimeout(warmRemaining, 500);
      }
    };

    const firstImage = displayed?.querySelector('img');

    if (firstImage?.complete && firstImage.naturalWidth) {
      scheduleWarmup();
    } else if (firstImage) {
      firstImage.addEventListener('load', scheduleWarmup, { once: true });
    }

    const controller = { show };
    galleryControllers.set(root, controller);

    return controller;
  };

  const showProductMedia = (root, mediaId) => {
    if (root && mediaId) getGalleryController(root)?.show(mediaId);
  };

  const initProduct = (root) => {
    getGalleryController(root);

    const variantsElement = root.querySelector('[data-product-variants]');
    const variantInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');

    if (!variantsElement || !variantInput || !addButton) return;

    let variants = [];

    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch {
      return;
    }

    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-product-compare-price]');
    const saleBadge = root.querySelector('[data-product-sale-badge]');
    const availability = root.querySelector('[data-product-availability]');
    const stickyPrice = document.querySelector(
      '[data-mobile-product-cta] [data-sticky-price]'
    );

    const selectedOptions = () => Array.from(
      root.querySelectorAll('fieldset[data-option-position]')
    )
      .sort(
        (a, b) =>
          Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      )
      .map(
        (group) => group.querySelector('[data-option-input]:checked')?.value || ''
      );

    const updateVariant = () => {
      const options = selectedOptions();

      const variant = options.length
        ? variants.find(
            (item) => item.options.every(
              (value, index) => value === options[index]
            )
          )
        : variants.find(
            (item) => String(item.id) === String(variantInput.value)
          ) || variants[0];

      if (!variant) {
        variantInput.value = '';
        addButton.disabled = true;
        addButton.textContent = addButton.dataset.unavailableLabel;

        if (availability) {
          availability.textContent = 'This combination is unavailable';
        }

        return;
      }

      variantInput.value = variant.id;

      if (price) price.textContent = variant.price;
      if (stickyPrice) stickyPrice.textContent = variant.price;

      if (comparePrice) {
        comparePrice.hidden = !variant.compare_at_price;
        comparePrice.textContent = variant.compare_at_price || '';
      }

      if (saleBadge) {
        saleBadge.hidden = !variant.compare_at_price;
      }

      addButton.disabled = !variant.available;

      addButton.textContent = variant.available
        ? `${addButton.dataset.addLabel} \u2192`
        : addButton.dataset.soldLabel;

      if (availability) {
        availability.textContent = variant.available
          ? 'In stock \xB7 Made to order'
          : 'Currently unavailable';
      }

      if (variant.featured_media_id) {
        showProductMedia(root, variant.featured_media_id);
      }

      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    };

    root.querySelectorAll('[data-option-input]').forEach((input) => {
      input.addEventListener('change', updateVariant);
    });

    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        showProductMedia(root, thumb.dataset.galleryThumb);
      });
    });

    root.querySelectorAll('[data-upload-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const text = input
          .closest('[data-upload-control]')
          ?.querySelector('[data-upload-text]');

        if (text && input.files?.length) {
          text.textContent = input.files[0].name;
        }
      });
    });
  };

  ready(() => {
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      const panel = document.getElementById(
        button.getAttribute('aria-controls')
      );

      if (!panel) return;

      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';

        button.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
        document.body.classList.toggle('menu-open', !open);
      });
    });

    document.querySelectorAll('[data-product-root]').forEach(initProduct);
  });
})();